import React, { useState } from 'react';
import BalancedHumanizer from '../utils/balancedHumanizer';
import { useLicense } from '../contexts/LicenseContext';
import './Humanizer.css';

const Humanizer = () => {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [originalText, setOriginalText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [humanizationLevel, setHumanizationLevel] = useState('balanced');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { checkFeatureAccess, incrementUsage, isTrialUser, getTrialInfo } = useLicense();
  const humanizer = new BalancedHumanizer();

  // Function to highlight changes between original and humanized text
  const highlightChanges = (original, humanized) => {
    if (!original || !humanized) return humanized;
    
    const originalWords = original.split(/(\s+)/);
    const humanizedWords = humanized.split(/(\s+)/);
    
    let result = [];
    let originalIndex = 0;
    let humanizedIndex = 0;
    
    while (humanizedIndex < humanizedWords.length) {
      const humanizedWord = humanizedWords[humanizedIndex];
      const originalWord = originalWords[originalIndex];
      
      // Check if word has changed
      if (originalWord && humanizedWord.toLowerCase() !== originalWord.toLowerCase()) {
        // Word changed - highlight it
        result.push(`<span class="highlight-change">${humanizedWord}</span>`);
        originalIndex++;
      } else if (originalWord && humanizedWord.toLowerCase() === originalWord.toLowerCase()) {
        // Word unchanged
        result.push(humanizedWord);
        originalIndex++;
      } else {
        // New word added
        result.push(`<span class="highlight-added">${humanizedWord}</span>`);
      }
      
      humanizedIndex++;
    }
    
    return result.join('');
  };

  const handleHumanize = async () => {
    if (!inputText.trim()) {
      alert('Please enter some text to humanize.');
      return;
    }
    
    // Check feature access
    const accessCheck = await checkFeatureAccess('humanize');
    if (!accessCheck.hasAccess) {
      alert(`Access denied: ${accessCheck.reason}`);
        return;
      }
      
    setIsProcessing(true);
    setOriginalText(inputText);
    
    try {
      // Simulate processing time for better UX
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      let result = inputText;
      
      // Apply balanced humanization
      const passes = humanizationLevel === 'light' ? 1 : 
                    humanizationLevel === 'balanced' ? 2 : 3;
      
      for (let i = 0; i < passes; i++) {
        result = humanizer.humanize(result);
      }
      
      setOutputText(result);
      
      // Increment usage for trial users
      await incrementUsage('humanize');
      
    } catch (error) {
      console.error('Humanization error:', error);
      alert('Error during humanization. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(outputText);
    alert('Text copied to clipboard!');
  };

  const handleClear = () => {
    setInputText('');
    setOutputText('');
    setOriginalText('');
  };

  const sampleTexts = [
    "The utilization of artificial intelligence in modern business operations has demonstrated significant potential for enhancing productivity and optimizing workflow efficiency. Organizations are increasingly implementing AI-driven solutions to facilitate decision-making processes and establish competitive advantages in their respective markets.",
    "It is important to note that the implementation of comprehensive digital transformation strategies requires careful consideration of various factors. Furthermore, organizations must ensure that their technological infrastructure is robust enough to support these initiatives effectively.",
    "The methodology employed in this research demonstrates a systematic approach to data analysis. Consequently, the results obtained provide valuable insights into the optimization of business processes and the enhancement of operational efficiency."
  ];

  const loadSample = (sample) => {
    setInputText(sample);
  };

  // Get trial info for display
  const [trialInfo, setTrialInfo] = useState(null);
  
  React.useEffect(() => {
    const fetchTrialInfo = async () => {
      if (isTrialUser()) {
        const info = await getTrialInfo();
        setTrialInfo(info);
      }
    };
    fetchTrialInfo();
  }, [isTrialUser, getTrialInfo]);

  return (
    <div className="humanizer">
      <div className="humanizer-header">
        <h2>🤖 Humanizer</h2>
        <p>AI content humanization with optimal balance between detection bypass and readability</p>
        <div className="balance-banner">
          ⚖️ Maintains readability while bypassing AI detection
      </div>
        
        {/* Trial Status Display */}
        {trialInfo && (
          <div className="trial-status-banner">
            <div className="trial-status-content">
              <span className="trial-icon">🎯</span>
              <div className="trial-status-text">
                <strong>Trial Active</strong>
                <span>Days remaining: {trialInfo.daysRemaining}</span>
                <span>Humanizations: {trialInfo.usage?.humanizations || 0}/100</span>
                  </div>
            </div>
            </div>
          )}
      </div>

      <div className="humanizer-controls">
        <div className="level-selector">
          <label>Humanization Level:</label>
          <select 
            value={humanizationLevel} 
            onChange={(e) => setHumanizationLevel(e.target.value)}
          >
            <option value="light">Light (1 pass)</option>
            <option value="balanced">Balanced (2 passes)</option>
            <option value="strong">Strong (3 passes)</option>
          </select>
        </div>

        <button 
          className="advanced-toggle"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? 'Hide' : 'Show'} Features
        </button>
      </div>

      {showAdvanced && (
        <div className="advanced-options">
          <h3>🤖 Humanization Features</h3>
          <div className="features-grid">
            <div className="feature-item">
              <h4>🎯 AI Phrase Targeting</h4>
              <p>Removes AI-typical phrases while maintaining readability</p>
              </div>
            <div className="feature-item">
              <h4>📖 Readable Synonyms</h4>
              <p>Replaces formal words with clear, readable alternatives</p>
              </div>
            <div className="feature-item">
              <h4>💬 Moderate Contractions</h4>
              <p>60% contraction usage for natural feel without overdoing it</p>
            </div>
            <div className="feature-item">
              <h4>🧠 Smart Word Replacement</h4>
              <p>Balanced vocabulary changes that maintain meaning</p>
            </div>
            <div className="feature-item">
              <h4>📝 Sentence Flow</h4>
              <p>Natural sentence structure without excessive fragmentation</p>
          </div>
            <div className="feature-item">
              <h4>🎲 Light Human Touch</h4>
              <p>Subtle human expressions without overwhelming the text</p>
            </div>
          </div>
        </div>
      )}

      <div className="sample-texts">
        <h3>Sample Texts (Click to Load)</h3>
        <div className="sample-buttons">
          {sampleTexts.map((sample, index) => (
            <button 
              key={index}
              className="sample-btn"
              onClick={() => loadSample(sample)}
            >
              Sample {index + 1}
            </button>
          ))}
        </div>
      </div>

      <div className="text-areas">
        <div className="input-section">
          <div className="section-header">
            <h3>Input Text</h3>
            <div className="char-count">
              {inputText.length} characters
            </div>
          </div>
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Paste your AI-generated text here..."
            className="text-input"
            rows="8"
          />
        </div>

        <div className="output-section">
          <div className="section-header">
            <h3>Humanized Output</h3>
            <div className="output-actions">
            <button 
                className="copy-btn"
                onClick={handleCopy}
              disabled={!outputText}
            >
                📋 Copy
            </button>
              <div className="char-count">
                {outputText.length} characters
              </div>
            </div>
          </div>
          <div 
            className="text-output-highlighted"
            dangerouslySetInnerHTML={{ 
              __html: highlightChanges(originalText, outputText) || 
                      '<span class="placeholder">Humanized text will appear here...</span>'
            }}
          />
        </div>
      </div>

      <div className="action-buttons">
        <button 
          className="humanize-btn"
          onClick={handleHumanize}
          disabled={!inputText.trim() || isProcessing}
        >
          {isProcessing ? '🤖 Humanizing...' : '🤖 Humanize Text'}
            </button>
        
        <button 
          className="clear-btn"
          onClick={handleClear}
        >
              🗑️ Clear All
            </button>
      </div>

      <div className="humanizer-info">
        <h3>🤖 How Humanization Works</h3>
        <div className="info-grid">
          <div className="info-item">
            <h4>1. AI Phrase Removal</h4>
            <p>Targets specific AI phrases while keeping text readable</p>
          </div>
          <div className="info-item">
            <h4>2. Readable Synonyms</h4>
            <p>Replaces formal words with clear, understandable alternatives</p>
          </div>
          <div className="info-item">
            <h4>3. Moderate Contractions</h4>
            <p>Uses contractions 60% of the time for natural feel</p>
          </div>
          <div className="info-item">
            <h4>4. Smart Restructuring</h4>
            <p>Improves sentence flow without excessive fragmentation</p>
          </div>
          <div className="info-item">
            <h4>5. Light Human Touch</h4>
            <p>Adds subtle human expressions without overwhelming</p>
          </div>
          <div className="info-item">
            <h4>6. Readability Cleanup</h4>
            <p>Removes excessive punctuation and fixes spacing</p>
          </div>
        </div>
      </div>

      <div className="balance-tips">
        <h3>⚖️ Tips for Best Results</h3>
        <div className="tips-grid">
          <div className="tip-item">
            <h4>✅ For AI Detection Bypass</h4>
            <ul>
              <li>Use contractions moderately</li>
              <li>Replace formal AI phrases</li>
              <li>Add natural sentence starters</li>
              <li>Vary sentence structure</li>
              <li>Use conversational language</li>
            </ul>
          </div>
          <div className="tip-item">
            <h4>✅ For Readability</h4>
            <ul>
              <li>Keep sentences clear and concise</li>
              <li>Use familiar vocabulary</li>
              <li>Maintain logical flow</li>
              <li>Avoid excessive fragmentation</li>
              <li>Preserve original meaning</li>
            </ul>
          </div>
            </div>
            </div>

      <div className="readability-info">
        <h3>📖 Readability Guidelines</h3>
        <div className="readability-grid">
          <div className="readability-item">
            <h4>Target Score: 70-80</h4>
            <p>Ideal for general audiences (13-15 year old level)</p>
            </div>
          <div className="readability-item">
            <h4>Avoid Score: Below 50</h4>
            <p>Too complex for most readers</p>
            </div>
          <div className="readability-item">
            <h4>Balance: AI Bypass + Readability</h4>
            <p>Maintains detection bypass while keeping text accessible</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Humanizer; 
